import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import EmailVerification from './components/EmailVerification';
import BackupCodeEntry from './components/BackupCodeEntry';
import ResetPasswordForm from './components/ResetPasswordForm';
import SuccessState from './components/SuccessState';

// Steps: 'email' | 'backup' | 'reset' | 'success'

const GeometricBackground = () => (
  <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
    {/* Large angular panel - top right */}
    <div style={{
      position: 'absolute',
      top: '-80px',
      right: '-120px',
      width: '500px',
      height: '500px',
      border: '1px solid rgba(0, 79, 152, 0.08)',
      borderRadius: 'var(--radius-sm)',
      transform: 'rotate(15deg)',
    }} />
    <div style={{
      position: 'absolute',
      top: '60px',
      right: '-60px',
      width: '300px',
      height: '300px',
      border: '1px solid rgba(255, 87, 34, 0.06)',
      borderRadius: 'var(--radius-sm)',
      transform: 'rotate(8deg)',
    }} />
    {/* Bottom left geometric */}
    <div style={{
      position: 'absolute',
      bottom: '-100px',
      left: '-80px',
      width: '400px',
      height: '400px',
      border: '1px solid rgba(0, 79, 152, 0.06)',
      borderRadius: 'var(--radius-sm)',
      transform: 'rotate(-12deg)',
    }} />
    {/* Accent line */}
    <div style={{
      position: 'absolute',
      top: 0,
      left: '50%',
      width: '1px',
      height: '100%',
      background: 'linear-gradient(to bottom, transparent, rgba(255,87,34,0.06), transparent)',
    }} />
    {/* Dot grid pattern */}
    <div style={{
      position: 'absolute',
      bottom: '80px',
      right: '40px',
      display: 'grid',
      gridTemplateColumns: 'repeat(6, 1fr)',
      gap: '12px',
      opacity: 0.3,
    }}>
      {Array.from({ length: 24 })?.map((_, i) => (
        <div key={i} style={{ width: '3px', height: '3px', borderRadius: '50%', background: 'var(--color-muted-foreground)' }} />
      ))}
    </div>
  </div>
);

const StepIndicator = ({ currentStep }) => {
  const steps = [
    { key: 'email', label: 'Verifiser' },
    { key: 'reset', label: 'Nytt passord' },
    { key: 'success', label: 'Fullført' },
  ];
  const stepOrder = ['email', 'backup', 'reset', 'success'];
  const currentIndex = stepOrder?.indexOf(currentStep);
  const displayIndex = currentStep === 'backup' ? 0 : currentIndex > 0 ? currentIndex - 1 : 0;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '40px' }}>
      {steps?.map((step, index) => {
        const isActive = index === displayIndex;
        const isCompleted = index < displayIndex;
        return (
          <React.Fragment key={step?.key}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                border: `2px solid ${isCompleted ? 'var(--color-accent)' : isActive ? 'var(--color-accent)' : 'rgba(156,163,175,0.3)'}`,
                background: isCompleted ? 'var(--color-accent)' : isActive ? 'rgba(255,87,34,0.12)' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                transition: 'all 250ms ease-out',
              }}>
                {isCompleted ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                ) : (
                  <span style={{ fontFamily: 'var(--font-data)', fontSize: '11px', fontWeight: 600, color: isActive ? 'var(--color-accent)' : 'var(--color-muted-foreground)' }}>{index + 1}</span>
                )}
              </div>
              <span style={{ fontFamily: 'var(--font-caption)', fontSize: '11px', fontWeight: isActive ? 600 : 400, color: isActive ? 'var(--color-foreground)' : 'var(--color-muted-foreground)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                {step?.label}
              </span>
            </div>
            {index < steps?.length - 1 && (
              <div style={{ flex: 1, height: '1px', background: index < displayIndex ? 'var(--color-accent)' : 'rgba(156,163,175,0.2)', transition: 'background 250ms ease-out', maxWidth: '40px' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const AccountRecovery = () => {
  const [step, setStep] = useState('email'); // email | backup | reset | success
  const [verifiedEmail, setVerifiedEmail] = useState('');

  const handleEmailVerified = (email) => {
    setVerifiedEmail(email);
    setStep('reset');
  };

  const handleBackupVerified = () => {
    setStep('reset');
  };

  const handlePasswordReset = () => {
    setStep('success');
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--color-background)',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      <GeometricBackground />

      {/* Header */}
      <header
        className="account-recovery-header"
        style={{
          position: 'relative',
          zIndex: 10,
          padding: '24px 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(156, 163, 175, 0.1)',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <Link to="/landing-page" style={{ display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none' }}>
          <div style={{
            width: '36px',
            height: '36px',
            background: 'rgba(0, 79, 152, 0.12)',
            border: '2px solid rgba(0, 79, 152, 0.3)',
            borderRadius: 'var(--radius-sm)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
          </div>
          <span style={{ fontFamily: 'var(--font-heading)', fontSize: '16px', fontWeight: 600, color: 'var(--color-foreground)', letterSpacing: '0.02em' }}>Veiassistent</span>
        </Link>
        <Link
          to="/login"
          style={{
            fontFamily: 'var(--font-caption)',
            fontSize: '13px',
            fontWeight: 500,
            color: 'var(--color-primary)',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'opacity 150ms ease-out',
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Tilbake til innlogging
        </Link>
      </header>

      {/* Main content */}
      <main style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 24px',
        position: 'relative',
        zIndex: 10,
      }}>
        <div style={{ width: '100%', maxWidth: '480px' }}>
          {/* Panel */}
          <div
            className="account-recovery-panel"
            style={{
              background: 'var(--color-card)',
              border: '2px solid rgba(156, 163, 175, 0.12)',
              borderRadius: 'var(--radius-md)',
              padding: '40px',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Top accent bar */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: 'linear-gradient(to right, var(--color-accent), var(--color-primary))',
            }} />

            {step !== 'success' && <StepIndicator currentStep={step} />}

            {step === 'email' && (
              <EmailVerification
                onVerified={handleEmailVerified}
                onSwitchToBackup={() => setStep('backup')}
              />
            )}
            {step === 'backup' && (
              <BackupCodeEntry
                onVerified={handleBackupVerified}
                onSwitchToEmail={() => setStep('email')}
              />
            )}
            {step === 'reset' && (
              <ResetPasswordForm
                userEmail={verifiedEmail}
                onSuccess={handlePasswordReset}
              />
            )}
            {step === 'success' && (
              <SuccessState
                message="Passord oppdatert!"
                subMessage="Passordet ditt er oppdatert. Du kan nå logge inn med det nye passordet."
              />
            )}
          </div>

          {/* Footer note */}
          {step !== 'success' && (
            <p style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', color: 'var(--color-muted-foreground)', textAlign: 'center', marginTop: '20px', lineHeight: 1.6 }}>
              Trenger du hjelp?{' '}
              <Link to="/contact" style={{ color: 'var(--color-primary)', textDecoration: 'none', fontWeight: 500 }}>Kontakt support</Link>
            </p>
          )}
        </div>
      </main>
    </div>
  );
};

export default AccountRecovery;
